import React from 'react'
import ReactDOM from 'react-dom/client'
import {
    createBrowserRouter,
    RouterProvider,
    Navigate,
    Outlet,
    useLocation,
} from 'react-router-dom'
import { initializeIcons } from '@fluentui/react'
import { AuthProvider } from './authContext'

import './index.css'

import Ask from './pages/ask/Ask'
import LayoutWrapper from './layoutWrapper'
import EmptyLayout from './pages/layout/EmptyLayout'
import PrivateRoutes from './components/PrivateRoutes'
import AdminRoute from './components/AdminRoute'
import SignedInRoute from './components/SignedInRoute'
import { useAuth } from './hooks/useAuth'
import { safeLocalStorage } from './utils/storage'
import { CommunityQuestionProvider } from './communityContext'
import { useConfig } from './contexts/ConfigContext'
import { useLandingPath, NOTHING_ENABLED_PATH } from './hooks/useLandingPath'
import RouteErrorBoundary from './components/ErrorBoundary/RouteErrorBoundary'

initializeIcons()

function AskRouteWrapper() {
    const { ask, isLoading, anonymousPageAccess } = useConfig()
    const { isAnonymous, loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (!ask || (isAnonymous && !anonymousPageAccess.chat)) {
        return <Navigate to={landing} replace />
    }

    return <Outlet />
}

function ContributeRouteWrapper() {
    const { contribute, isLoading } = useConfig()
    const { loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (!contribute) {
        return <Navigate to={landing} replace />
    }

    return <Outlet />
}
function CommunityHubRouteWrapper() {
    const { communityHub, isLoading, anonymousPageAccess } = useConfig()
    const { isAnonymous, loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (!communityHub || (isAnonymous && !anonymousPageAccess.community)) {
        return <Navigate to={landing} replace />
    }

    return <Outlet />
}

function AccountSettingsRouteWrapper() {
    const { isAnonymous, loading } = useAuth()

    if (loading) {
        return <div />
    }

    if (isAnonymous) {
        return <Navigate to="/login" replace />
    }

    return <Outlet />
}

function ProfileRouteWrapper() {
    const { profilepage, isLoading } = useConfig()
    const { isAuthenticated, isAnonymous, loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (!profilepage || !isAuthenticated || isAnonymous) {
        return <Navigate to={landing} replace />
    }

    return <Outlet />
}

function WorkspacesRouteWrapper() {
    const { isAuthenticated, isAnonymous, loading } = useAuth()
    const location = useLocation()

    if (loading) return <div />

    if (!isAuthenticated && !isAnonymous) {
        if (location.search.includes('join=')) {
            safeLocalStorage.setItem(
                'intendedUrl',
                location.pathname + location.search
            )
        }
        return <Navigate to="/login" replace />
    }

    return <EmptyLayout />
}

function EngageRouteWrapper() {
    const { engage, isLoading } = useConfig()
    const { loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (!engage) {
        return <Navigate to={landing} replace />
    }

    return <Outlet />
}

function CapturesRouteWrapper() {
    const { captures, isLoading } = useConfig()
    const { loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (!captures) {
        return <Navigate to={landing} replace />
    }

    return <SignedInRoute />
}

// Captures used to live at /session-summaries. Saved reports and shared links
// still point there, so keep the rest of the path and forward it.
function LegacySessionSummariesRedirect() {
    const { pathname, search } = useLocation()
    const rest = pathname.replace(/^\/session-summaries/, '')
    return <Navigate to={`/captures${rest}${search}`} replace />
}

// Shown when a tenant has every page switched off, so the route wrappers have
// somewhere to send people instead of redirecting to each other.
function NothingEnabledRouteWrapper() {
    const { isLoading } = useConfig()
    const { loading } = useAuth()
    const landing = useLandingPath()

    if (isLoading || loading) {
        return <div />
    }

    if (landing !== NOTHING_ENABLED_PATH) {
        return <Navigate to={landing} replace />
    }

    return <Outlet />
}

const router = createBrowserRouter([
    {
        path: '/login',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/login/Login').then((m) => ({
                        Component: m.default,
                    })),
            },
        ],
    },
    {
        path: '/register',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/register/Register').then((m) => ({
                        Component: m.default,
                    })),
            },
        ],
    },
    {
        path: '/verify',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/verify/Verify').then((m) => ({
                        Component: m.default,
                    })),
            },
        ],
    },
    {
        path: '/forgot-password',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/forgot-password/ForgotPassword').then(
                        (m) => ({
                            Component: m.default,
                        })
                    ),
            },
        ],
    },
    {
        path: '/reset-password',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/reset-password/ResetPassword').then(
                        (m) => ({
                            Component: m.default,
                        })
                    ),
            },
        ],
    },
    {
        path: '/access-denied',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/NoPage').then((module) => {
                        return { Component: module.AccessDenied }
                    }),
            },
        ],
    },
    {
        path: '/select-tenant',
        element: <Navigate to="/workspaces" replace />,
    },
    {
        path: '/workspaces',
        element: <WorkspacesRouteWrapper />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/workspaces/Workspaces').then((m) => ({
                        Component: m.default,
                    })),
            },
        ],
    },
    {
        path: '/q/:code',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/qr-redeem/QrRedeem').then((m) => ({
                        Component: m.default,
                    })),
            },
        ],
    },
    {
        element: <PrivateRoutes />,
        // Covers every signed-in page (Ask, Contribute, Community, …). Without
        // it a render fault anywhere in this subtree falls through to React
        // Router's bare default screen and the whole app is unusable until the
        // user reloads by hand.
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                element: <LayoutWrapper />,
                children: [
                    {
                        path: '/',
                        element: <AskRouteWrapper />,
                        children: [{ index: true, element: <Ask /> }],
                    },
                    {
                        path: '/contribute',
                        element: <ContributeRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/contribute/Contribute'),
                            },
                        ],
                    },
                    {
                        path: '/contribute/:categoryId',
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/contribute/Contribute'),
                            },
                        ],
                    },
                    {
                        path: NOTHING_ENABLED_PATH,
                        element: <NothingEnabledRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/nothingEnabled/NothingEnabled').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/accountSettings',
                        element: <AccountSettingsRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/accountSettings/accountSettings').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/admin',
                        element: <AdminRoute />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/admin/AdminHome').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'questions',
                                lazy: () =>
                                    import('./pages/admin/AdminQuestions').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'polls',
                                lazy: () =>
                                    import('./pages/admin/AdminPolls').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'exchange',
                                lazy: () =>
                                    import('./pages/admin/AdminExchange').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'captures',
                                lazy: () =>
                                    import('./pages/admin/captures/CapturesPage').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'captures/:groupId',
                                lazy: () =>
                                    import('./pages/admin/captures/CaptureGroupPage').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'captures/single/:captureId',
                                lazy: () =>
                                    import('./pages/admin/captures/CapturePage').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'captures/:groupId/:captureId',
                                lazy: () =>
                                    import('./pages/admin/captures/CapturePage').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/session-summaries/*',
                        element: <LegacySessionSummariesRedirect />,
                    },
                    {
                        path: '/captures',
                        element: <CapturesRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/sessionSummaries/sessionSummaries').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'log',
                                lazy: () =>
                                    import('./pages/sessionsLog/sessionsLog').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                            {
                                path: 'log/:sessionId',
                                lazy: () =>
                                    import('./pages/sessionSummaryDetail/sessionSummaryDetail').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/profile/:handle',
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/publicProfile/PublicProfile').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/profile',
                        element: <ProfileRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/profile/Profile').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/community',
                        element: <CommunityHubRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/community/Community').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/members',
                        element: <CommunityHubRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/membersDirectory/membersDirectory').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/exchange',
                        element: <CommunityHubRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/exchange/Exchange').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/engage',
                        element: <EngageRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/engage/Engage').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: 'engage/:view',
                        element: <EngageRouteWrapper />,
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/engage/Engage').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                    {
                        path: '/polls-present',
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import('./pages/presentPolls/PresentPolls').then(
                                        (module) => ({
                                            Component: module.default,
                                        })
                                    ),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '*',
        element: <EmptyLayout />,
        children: [
            {
                index: true,
                lazy: () =>
                    import('./pages/NoPage').then((module) => {
                        return { Component: module.default }
                    }),
            },
        ],
    },
])

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <AuthProvider>
            <CommunityQuestionProvider>
                <RouterProvider router={router} />
            </CommunityQuestionProvider>
        </AuthProvider>
    </React.StrictMode>
)
